import { useAuth } from '@/hooks'
import { hasPermission } from '@/hooks/useBackendMenu'
import {
    compensacaoManual,
    CreateCompensacaoManual,
    createCompensacaoManualSchema,
} from '@/service/api'
import { useStore } from '@/store'
import { TipoCompensacaoManual } from '@acme/core'
import dayjs from 'dayjs'
import Dialog from 'global/guides/Dialog'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import FormCompensacaoManual from '../../CompensacaoManual/forms/FormCompensacaoManual'
import { useUpdateDigitacaoQueryData } from '../pages/useDigitacaoData'

export default function NovaCompensacaoManual() {
    const { setQueryData } = useUpdateDigitacaoQueryData()
    const navigate = useNavigate()
    const { mutateAsync, isLoading } = compensacaoManual.mutations.useCreate()
    const funcionarioId = useStore(state => state.employeeId)
    const empresaId = useStore(state => state.companyId)
    const competencia = useStore(state => state.competencia)
    const { decoded } = useAuth()
    const userId = decoded?.sub

    const params = useParams<{ dia: string }>()
    const [searchParams] = useSearchParams()
    const dia = dayjs.utc(params.dia!)

    // Lê os search params para determinar qual campo preencher
    const tipos = searchParams.getAll('tipo') // Pode ter 'falta' e/ou 'extra'
    const temFalta = tipos.includes('falta')
    const temExtra = tipos.includes('extra')

    // Define valores padrão baseado nos search params
    const getDefaultValues = () => {
        const defaults: any = {
            data: dia.format('YYYY-MM-DD'),
            destinos: [],
            empresaId,
            funcionarioId,
            competencia,
        }

        // Se tem falta, tipo COMPENSAR (dia com falta precisa ser compensado)
        if (temFalta && !temExtra) {
            defaults.tipo = TipoCompensacaoManual.COMPENSADO
        }
        // Se tem extra, tipo COMPENSADO (dia com extra compensa outro)
        else if (temExtra && !temFalta) {
            defaults.tipo = TipoCompensacaoManual.COMPENSAR
        }
        // Se tem ambos (falta e extra), padrão COMPENSAR
        else if (temFalta && temExtra) {
            defaults.tipo = TipoCompensacaoManual.COMPENSAR
        }
        // Se não tem nada, não define o tipo (usuário escolhe)

        return defaults
    }

    function handleSubmit(data: CreateCompensacaoManual) {
        if (!userId || !funcionarioId) {
            toast.error('Selecione um funcionário')
            return
        }

        if (!empresaId) {
            toast.error('Selecione uma empresa')
            return
        }

        return mutateAsync(
            {
                ...data,
                empresaId,
                funcionarioId,
            },
            {
                onSuccess: res => {
                    toast.success('Compensação manual criada com sucesso!')
                    setQueryData('COMPENSACAO_MANUAL', res)
                    navigate(-1)
                },
                onError: err => {
                    toast.error(
                        err.message || 'Erro ao criar compensação manual',
                    )
                },
            },
        )
    }

    return (
        <Dialog.Root
            width="2xl"
            onSubmit={handleSubmit}
            loading={isLoading}
            schema={createCompensacaoManualSchema}
            open={hasPermission(['create', 'CompensaçãoManual'])}
            defaultValues={getDefaultValues()}
        >
            <Dialog.Title>Nova compensação manual</Dialog.Title>
            <Dialog.Content>
                <FormCompensacaoManual />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton />
            </Dialog.Actions>
        </Dialog.Root>
    )
}
